import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Recycle, 
  Award, 
  Truck, 
  ArrowRight,
  Car,
  CheckCircle
} from 'lucide-react';
import USAFlag from '../icons/USAFlag';

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  const heroTabs = [
    {
      title: "Better for your car",
      subtitle: "Premium Protection",
      description: "100% recycled materials engineered for superior durability, precision fit, and maximum vehicle protection.",
      highlight: "Premium Quality",
      color: "automats-red"
    },
    {
      title: "Better for your margins",
      subtitle: "Dealer Success",
      description: "2-3x better profit margins than big brands. American-made means faster delivery and no tariff complications.",
      highlight: "2-3x Margins",
      color: "automats-steel"
    },
    {
      title: "Better for the planet",
      subtitle: "Eco Excellence", 
      description: "Zero virgin content. Every mat transforms post-consumer waste into premium automotive protection.",
      highlight: "Zero Waste",
      color: "automats-eco-green"
    }
  ];

  return (
    <section className="py-20 lg:py-24 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <Badge className="bg-automats-eco-green/20 text-automats-eco-green border-automats-eco-green/30 text-sm px-4 py-2 font-body">
                <Recycle className="h-4 w-4 mr-2" />
                100% Recycled Materials
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-semibold leading-tight text-automats-black">
                {heroTabs[activeTab].title}
              </h1>
              
              <div className="text-2xl font-semibold text-automats-red">
                {heroTabs[activeTab].subtitle}
              </div>
              
              <p className="text-xl text-automats-black leading-relaxed max-w-2xl">
                {heroTabs[activeTab].description}
              </p>
            </div>

            {/* Interactive Tabs */}
            <div className="flex flex-wrap gap-3">
              {heroTabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`px-6 py-3 rounded-lg font-body font-medium transition-all duration-300 ${
                    activeTab === index 
                      ? `bg-${tab.color} text-white shadow-lg` 
                      : 'bg-white border-2 border-gray-200 text-automats-steel hover:border-automats-red'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Key Features */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center space-x-2 text-automats-black">
                <div className="w-6 h-6 rounded-full bg-automats-red flex items-center justify-center">
                  <Award className="h-4 w-4 text-white" />
                </div>
                <span>40+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2 text-automats-black">
                <div className="w-6 h-6 rounded-full bg-automats-red flex items-center justify-center">
                  <USAFlag className="h-4 w-4" />
                </div>
                <span>Made in USA</span>
              </div>
              <div className="flex items-center space-x-2 text-automats-black">
                <div className="w-6 h-6 rounded-full bg-automats-eco-green flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
                <span>Zero Virgin Content</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center sm:justify-start">
              <a 
                href="mailto:sales@automats.us?subject=Sample Request&body=Hello, I would like to request product samples to evaluate."
                className="btn-clean btn-premium text-white hover:bg-red-700 text-lg font-medium"
              >
                Request Sample Kit
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Right Content - Interactive Visual */}
          <div className="relative">
            <div className="hero-card relative rounded-2xl shadow-xl overflow-hidden">
              {activeTab === 0 && (
                <img 
                  src="/images/better-for-car.webp" 
                  alt="Better for your car"
                  className="w-full h-full object-contain rounded-2xl"
                />
              )}
              {activeTab === 1 && (
                <img 
                  src="/images/better-for-margins.webp" 
                  alt="Better for your margins"
                  className="w-full h-full object-contain rounded-2xl"
                />
              )}
              {activeTab === 2 && (
                <img 
                  src="/images/better-for-planet.webp" 
                  alt="Better for the planet"
                  className="w-full h-full object-contain rounded-2xl"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;